import { useQuery } from '@tanstack/react-query';

import { getMemory, type MemorySection } from '../api/memory';

type UseMemoryDocumentOptions = {
  baseUrl: string;
  userId?: string | null;
  enabled?: boolean;
};

export const MEMORY_QUERY_KEY = ['memory'] as const;

export function useMemoryDocument({ baseUrl, userId, enabled = true }: UseMemoryDocumentOptions) {
  const isEnabled = enabled && Boolean(userId);

  const {
    data,
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: [...MEMORY_QUERY_KEY, userId],
    queryFn: () => getMemory(baseUrl),
    enabled: isEnabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: 'always',
  });

  const sections: MemorySection[] = data?.sections ?? [];

  return {
    sections,
    text: sectionsToText(sections),
    error: queryError?.message ?? null,
    isLoading,
    updatedAt: data?.updatedAt ?? null,
  };
}

// Flattened unique memory text — drives the dashboard char count, so a memory
// shown under several sections is counted once.
function sectionsToText(sections: MemorySection[]): string {
  const seen = new Set<string>();
  for (const section of sections) for (const entry of section.entries) seen.add(entry.text);
  return [...seen].join('\n');
}
